import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Alert, Box, Button, Stack, TextField } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useWizardStepContext from 'v2/components/WizardModal/useWizardStepContext';
import { DEVICE_GROUP_NAME_MAX_LENGTH } from 'v2/constants';
import { getDeviceGroupPath } from 'v2/paths';
import {
  createMaxLengthValidator,
  createNoSpecialCharsValidator,
  createNotWhitespaceOnlyValidator,
  createUniqueValidator
} from 'v2/utils/validation';

const FORM_ID = 'device-group-name-step-form';
const STEP_INDEX = 0;

interface FormValues {
  name: string;
}

interface Props {
  existingNames: string[];
  onCreate: (groupId: string, groupName: string) => void;
  createDeviceGroup: (name: string) => Promise<{ success: boolean; groupId?: string; errorMessage?: string }>;
}

const DeviceGroupNameStep: React.FC<Props> = ({ existingNames, onCreate, createDeviceGroup }) => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const { goToNext, setFooterContent, steps } = useWizardStepContext();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormValues>({ mode: 'onTouched', defaultValues: { name: '' } });

  const onSubmit = handleSubmit(async (data) => {
    setIsLoading(true);
    try {
      const result = await createDeviceGroup(data.name);
      if (result.success && result.groupId) {
        onCreate(result.groupId, data.name);
        navigate(getDeviceGroupPath(result.groupId));
        goToNext();
      } else {
        showSnackbar(result.errorMessage ?? translation.an_error_occurred, { severity: 'error' });
      }
    } finally {
      setIsLoading(false);
    }
  });

  React.useLayoutEffect(() => {
    setFooterContent(
      STEP_INDEX,
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, gap: 2 }}>
        <Button
          key="device-group-name-step-submit"
          id="device-group-name-submit-button"
          variant="contained"
          type="submit"
          form={FORM_ID}
          loading={isLoading}
          endIcon={<ChevronRightIcon />}
        >
          {translation.create_and_set_up}
        </Button>
      </Box>
    );
  }, [isLoading, setFooterContent, steps, translation]);

  return (
    <Stack component="form" id={FORM_ID} onSubmit={onSubmit} noValidate gap={3}>
      <TextField
        {...register('name', {
          required: translation.name_cannot_be_empty,
          validate: {
            notWhitespaceOnly: createNotWhitespaceOnlyValidator(translation.group_name_empty_error),
            noSpecialChars: createNoSpecialCharsValidator(translation.group_name_special_chars_error),
            maxLength: createMaxLengthValidator(DEVICE_GROUP_NAME_MAX_LENGTH, translation.group_name_too_long_error),
            unique: createUniqueValidator(existingNames, translation.group_name_in_use_error)
          }
        })}
        id="name-input"
        label={translation.group_name}
        autoFocus
        fullWidth
        error={!!errors.name}
        helperText={errors.name?.message}
      />
      <Alert severity="info">{translation.new_group_wizard_info}</Alert>
    </Stack>
  );
};

export default DeviceGroupNameStep;
